import { type ReactElement } from 'react';
import { LoadingContainer } from '@ff-it/ui';
import { useNavigate } from 'react-router-dom';
import { useModel } from 'components';
import { PermissionDenied } from 'components/scenes/errors';
import { useHasPerms } from 'core/permissions';
import { useProductCollection } from './productCollection';
import { ProductKindSection } from './ProductKindSection';
import { productKindSectionsFor, productsByKind } from './kindSections';
import { getProductColumns } from './columns';
import type { OutletOrServiceCategoryProduct } from './types';
import type { OutletOrServiceCategory } from '../outletOrServiceCategory/types';
import type { TableColumns } from '@ff-it/ui';
import type { ActivityType } from 'types';
import type { ProductListRow } from './productListColumns';
import { useProductDrawerState } from './drawerState';
import { OutletProductDrawer, type OutletProductCreateSeed } from './OutletProductDrawer';
import type { ProductFormConfig } from './types';
import { productFormValuesFromProduct } from './formValues';

type ProductCollectionIndexProps = {
  outletOrServiceCategory: OutletOrServiceCategory;
  type: ActivityType;
  typeColumns?: TableColumns<OutletOrServiceCategoryProduct>;
  formConfig?: ProductFormConfig;
};

export function ProductCollectionIndex({
  outletOrServiceCategory,
  type,
  typeColumns,
  formConfig,
}: ProductCollectionIndexProps): ReactElement {
  const { endpoint, permissions } = useModel<OutletOrServiceCategoryProduct>();
  const [hasIndex, hasCreate, hasChange] = useHasPerms(permissions?.view, permissions?.add, permissions?.change);
  const collection = useProductCollection<OutletOrServiceCategoryProduct>(endpoint, type);
  const { products, isLoading, error } = collection;
  const drawer = useProductDrawerState<OutletProductCreateSeed>();
  const navigate = useNavigate();
  const sections = productKindSectionsFor(outletOrServiceCategory.kind);
  const groupedProducts = productsByKind(products);

  const openProduct = (product: OutletOrServiceCategoryProduct) => {
    if (hasChange) drawer.open({ mode: 'edit', productId: product.id });
    else navigate(String(product.id));
  };

  if (!hasIndex) return <PermissionDenied />;
  if (error) throw error;

  return (
    <LoadingContainer loading={isLoading} className="flex-grow-1 flex-column d-flex">
      {sections.map((meta) => {
        const sectionProducts = groupedProducts.get(meta.kind) ?? [];
        const rows: ProductListRow<OutletOrServiceCategoryProduct>[] = sectionProducts.map((product) => ({
          product,
          listPrice: null,
        }));
        return (
          <ProductKindSection
            key={meta.kind}
            meta={meta}
            count={sectionProducts.length}
            onCreate={hasCreate ? () => drawer.open({ mode: 'create', seed: { kind: meta.kind } }) : undefined}
            rows={rows}
            columns={getProductColumns(typeColumns, openProduct, meta.kind)}
            getRowId={({ product }) => String(product.id)}
          />
        );
      })}
      <OutletProductDrawer
        endpoint={endpoint}
        outletOrServiceCategory={outletOrServiceCategory}
        type={type}
        formConfig={formConfig}
        request={drawer.request}
        onClose={drawer.close}
        onSaved={() => void collection.mutate()}
        onCopy={(product) =>
          drawer.open({
            mode: 'create',
            seed: {
              kind: product.kind,
              initialValues: productFormValuesFromProduct(product),
            },
          })
        }
      />
    </LoadingContainer>
  );
}
